import { useCallback, useContext } from 'react';
import RangeSlider from '@/FiltersMenu/RangeSlider';
import FilterEvents from '~/constants/events';
import { useHasSoftPull } from '~/contexts/FinanceAndTradeContext';
import { useIsFinanceInitialized } from '~/contexts/FinanceAndTradeContext/FinanceAndTradeContext';
import { useTrackEvent } from '~/contexts/SearchAnalyticsContext';
import SearchContext from '~/contexts/SearchContext';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import FinancingCard from './FinancingCard';
import BudgetContext from '../BudgetContext';
import TradeCard from '../TradeCard';

type OnUpdate = (_?: number[]) => void;

function getMonthlyPaymentDescription({
  hasValidSoftPull,
  isFinanceInitialized,
  maxMonthlyPayment
}: {
  hasValidSoftPull: boolean;
  isFinanceInitialized: boolean;
  maxMonthlyPayment: number;
}) {
  return isFinanceInitialized && hasValidSoftPull
    ? `Your personalized financing terms limit your monthly payment to no more than $${formatAndDropDecimal(
        maxMonthlyPayment
      )}.`
    : undefined;
}

function getCashDownDescription({
  hasValidSoftPull,
  isFinanceInitialized,
  minDownPayment
}: {
  hasValidSoftPull: boolean;
  isFinanceInitialized: boolean;
  minDownPayment: number;
}) {
  if (!isFinanceInitialized || !hasValidSoftPull) return undefined;

  return minDownPayment === 0
    ? 'Your personalized financing terms do not require a minimum down payment.'
    : `Your personalized financing terms require a minimum down payment of $${formatAndDropDecimal(
        minDownPayment
      )} or more.`;
}

type OwnProps = {
  isShowing: boolean;
};

export default function BudgetFinance({ isShowing }: OwnProps) {
  const {
    currentDownPayment,
    currentMonthlyPayment,
    maxMonthlyPayment,
    minDownPayment,
    setCurrentDownPayment,
    setCurrentMonthlyPayment
  } = useContext(BudgetContext);
  const { showFinance } = useContext(SearchContext);
  const hasValidSoftPull = useHasSoftPull();
  const isFinanceInitialized = useIsFinanceInitialized();
  const trackEvent = useTrackEvent();

  const onUpdateDownPayment = useCallback<OnUpdate>(
    range => {
      const nextValue = range?.at(0);

      setCurrentDownPayment(nextValue);
      trackEvent(FilterEvents.cashDown, {
        previousValue: currentDownPayment,
        nextValue
      });
    },
    [currentDownPayment, setCurrentDownPayment, trackEvent]
  );

  const onUpdateMonthlyPayment = useCallback<OnUpdate>(
    range => {
      const nextValue = range?.at(0);

      setCurrentMonthlyPayment(nextValue);
      trackEvent(FilterEvents.monthlyPayment, {
        previousValue: currentMonthlyPayment,
        nextValue
      });
    },
    [currentMonthlyPayment, setCurrentMonthlyPayment, trackEvent]
  );

  return (
    <>
      <div className="gap-32 grid">
        <div>
          <RangeSlider
            isShowing={isShowing}
            id="monthly-payment"
            label="Max Monthly Payment"
            description={getMonthlyPaymentDescription({
              hasValidSoftPull,
              isFinanceInitialized,
              maxMonthlyPayment
            })}
            format="dollars"
            labelTextTransform="uppercase"
            min={0}
            max={maxMonthlyPayment}
            step={5}
            initialValues={[currentMonthlyPayment ?? maxMonthlyPayment]}
            onUpdate={onUpdateMonthlyPayment}
          />
        </div>

        <div>
          <RangeSlider
            isShowing={isShowing}
            id="down-payment"
            label="Max Cash Down Payment"
            description={getCashDownDescription({
              hasValidSoftPull,
              isFinanceInitialized,
              minDownPayment
            })}
            format="dollars"
            labelTextTransform="uppercase"
            min={minDownPayment}
            max={minDownPayment + 10000}
            step={500}
            initialValues={[currentDownPayment ?? minDownPayment]}
            onUpdate={onUpdateDownPayment}
          />
        </div>
      </div>

      {showFinance && <FinancingCard />}

      <TradeCard />
    </>
  );
}
